import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import Script from "next/script";
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { HotelIcon, RestaurantIcon, TicketIcon } from '@kursk/components/ui/icons';
import { getButtonProps, getButtonPropsForHotelOrRestaurant } from './getButtonProps';
import { api } from '@common/urls';
import { getEsiaProfileFields } from '@common/utils/esia';
import { useSelectSlice } from "@common/redux/selectors/useSelectSlice";
import { authSlice } from "@common/redux/slices/auth.slice";
import { EntityType, ITicketsInfo, IBuyButtonEventExtraProps } from "@common/redux/types/common";
import { IEventInfo } from "@common/redux/types/event";
import { priceToString } from "@common/utils/money";
import { getVenueId } from "@common/utils/entities";

import style from './BuyButton.module.scss';
import { getConfig } from "@root/config/config";
import { isTicketsInfoAvailable } from "@common/utils/tickets";
import { getEntitySeoTicketsType, seoBuyButtonClickHandler, seoShowButtonHandler } from '@kursk/components/seo/utils';
import { MuzaWidgetWrapper } from './muzaWidget/MuzaWidgetWrapper';
import { checkCanOpenMuzaWidget } from './muzaWidget/helpers/helpers';

interface IProps {
  withSeparator?: boolean;
  wrapperClassName?: string;
  buttonClassName?: string;
  hintBackgroundTheme?: 'dark' | 'light';
  entityType: EntityType;
  entityId: number;
  defaultDate?: number;
  venueId?: string;
  filterDates?: {
    start: number;
    end: number;
  }
  disabled?: boolean;
  position?: string;
  dataTestId?: string;
  eventExtraProps?: IBuyButtonEventExtraProps;
  customTicketsInfos?: ITicketsInfo[];
  isBenefitZ?: boolean;
}

type EntityTypeHash = {
  [p in EntityType]?: (id: number) => string;
}

export function BuyButton({
  withSeparator = true,
  entityType,
  entityId,
  wrapperClassName,
  buttonClassName,
  hintBackgroundTheme = 'dark',
  defaultDate,
  venueId,
  filterDates,
  disabled: disabledProps = false,
  position = 'header',
  dataTestId,
  eventExtraProps,
  customTicketsInfos,
  isBenefitZ = false,
}: IProps) {
  const [entity, setEntity] = useState(undefined);
  const [widgetLoader, setWidgetLoaded] = useState(false);
  const seoVisited = useRef(false);

  const { user } = useSelectSlice(authSlice);

  const entityTypesUrls: EntityTypeHash = {
    events: api.events.getOne,
    places: api.places.getOne,
    hotels: api.places.getOne,
    restaurants: api.places.getOne,
    overviews: api.articles.getOne,
    regionvideos: api.articles.getOne,
    ticketsbundles: api.ticketsbundles.getOne,
    streams: api.streams.getOne,
    touristroutes: api.touristroutes.getOne,
    routes: api.articles.getOne,
    movies: api.movies.getOne,
    excursions: api.excursions.getOne,
    tours: api.tours.getOne,
  };

  async function loadEntity(entityType: EntityType, entityId: number) {
    try {
      const apiFunc = entityTypesUrls[entityType];
      const response = await axios.get(apiFunc(entityId));
      const data = response?.data;
      if (!data) {
        throw new Error(`Entity ${entityType} with id=${entityId} not found`);
      }
      const field = Object.keys(data).filter(key => key !== 'total').pop();
      setEntity(data[field]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  function checkWidget() {
    if (window['CultregSales'] && window['CultregSales'].init) {
      window['CultregSales'].init({
        baseUrl: getConfig().backend.url,
        container: '#buy-widget',
        theme: getConfig().widgetTheme,
        ...user?.socials?.esia && getEsiaProfileFields(user),
      });
      setWidgetLoaded(true);
    } else {
      setTimeout(() => checkWidget(), 200);
    }
  }

  useEffect(() => {
    loadEntity(entityType, entityId);
  }, [entityType, entityId]);

  useEffect(() => {
    checkWidget();
  }, []);

  const getSeanceTicketsInfo = (seance: IEventInfo) => (
    isBenefitZ ? seance.ticketsInfoBenefitZ : seance.ticketsInfo
  );

  const ticketsInfosAll: ITicketsInfo[] = customTicketsInfos || (entity?.actualSeances?.all ?
    entity.actualSeances.all.map(getSeanceTicketsInfo).filter(Boolean) as ITicketsInfo[] :
    entity?.ticketsInfo ? [entity.ticketsInfo] : []);

  let ticketsInfos: ITicketsInfo[] = [...ticketsInfosAll];
  let prices = [entity?.maxPrice, entity?.minPrice];

  if (entityType === 'ticketsbundles') {
    ticketsInfos = [{
      unlimited: entity?.status === 'available',
      available: 0,
      total: 0,
      minPrice: entity?.minPrice,
      maxPrice: entity?.maxPrice,
      allPrices: [entity?.minPrice, entity?.maxPrice],
    }]
    prices = [entity?.minPrice, entity?.maxPrice];
  }

  if (venueId) {
    const filteredSeances = entity?.actualSeances?.all.filter((s: IEventInfo) => {
      const seanceTicketsInfo = getSeanceTicketsInfo(s);
      if (!seanceTicketsInfo || !isTicketsInfoAvailable(seanceTicketsInfo)) return false;
      if (getVenueId(s.venue) !== venueId) return false;
      if (filterDates) {
        return moment(s.periodStart).startOf('day').valueOf() === filterDates.start;
      }
      return true;
    });
    ticketsInfos = filteredSeances?.map(getSeanceTicketsInfo).filter(Boolean) as ITicketsInfo[];
  }

  const buyButtonEventExtraProps: IBuyButtonEventExtraProps = {
    ...eventExtraProps,
    ...(isBenefitZ && { isBenefitZ: true }),
  };

  const getExperimentalSaleLink: () => string = () => {
    if (ticketsInfosAll.find(t => isTicketsInfoAvailable(t))) return '';
    if (venueId && entity?.placesSaleLinks) {
      return entity?.placesSaleLinks?.find((place) => place.placeId.toString() === venueId)?.externalSaleLink || '';
    }
    return entity?.actualExternalSaleLink || '';
  };

  const externalSaleLink: string = getExperimentalSaleLink();
  const isFree: boolean = entity?.isFree || false;
  const isHotel = entity?.type === 'hotel';
  const isRestaurant = entity?.type === 'restaurant';
  const isPublished = entity?.isPublished;

  const {
    text,
    price,
    hint: hintText,
    empty = false,
    disabled = false,
    extraHintStyles = null,
    onClick = null,
  } = (isHotel || isRestaurant) ?
    getButtonPropsForHotelOrRestaurant(
      isHotel, isRestaurant, externalSaleLink,
      entity?.roomCost, entity?.averageCheck
    ) :
    getButtonProps(
      ticketsInfos,
      externalSaleLink,
      isFree,
      entity?.eipskPrices,
      entityId,
      entityType,
      prices,
      defaultDate,
      buyButtonEventExtraProps,
    );

  /** Функция нужна, для того чтобы определить есть ли в другой обычной кнопке hint,
   * который увеличивает размер кнопки по ширине. Это нужно для того, чтобы кнопка "Участникам СВО" повторяла размер
   * обычной кнопки, если такая есть **/
  const hasRegularButtonHint = (() => {
    if (!isBenefitZ || isHotel || isRestaurant) {
      return false;
    }

    const regularTicketsInfos: ITicketsInfo[] = customTicketsInfos || (entity?.actualSeances?.all
      ? entity.actualSeances.all.map((seance: IEventInfo) => seance.ticketsInfo).filter(Boolean)
      : entity?.ticketsInfo ? [entity.ticketsInfo] : []);

    const { hint } = getButtonProps(
      regularTicketsInfos,
      externalSaleLink,
      isFree,
      entity?.eipskPrices,
      entityId,
      entityType,
      prices,
      defaultDate,
      eventExtraProps,
    );

    return Boolean(hint);
  })();

  useEffect(() => {
    if (entity && widgetLoader) {
      const ticketsType = getEntitySeoTicketsType(entityType, entity);
      if (ticketsType && !seoVisited.current) {
        seoShowButtonHandler({
          entityType, entity, isMuzaWidget: checkCanOpenMuzaWidget(externalSaleLink),
        });
        seoVisited.current = true;
      }
    }
  }, [widgetLoader, entity, externalSaleLink]);

  const isWidgetDisabled = empty && _.compact([text, price, hintText]).length === 0;

  const soldout = entity?.status === 'stopped';

  const onButtonClick = (event: MouseEvent) => {
    event.preventDefault();

    seoBuyButtonClickHandler({
      entity, entityType, position, isMuzaWidget: checkCanOpenMuzaWidget(externalSaleLink) });
    if (!disabled && !disabledProps && !soldout) {
      onClick(user);
    }
  }

  if (!widgetLoader || !entity) {
    return <>
      <Script
        async
        type="text/javascript"
        src={`${getConfig().backend.url}/scripts/widgets/api.bundle.js`}
        id="widget-api"
      />
    </>;
  }

  if (isWidgetDisabled || !isPublished) {
    return <></>;
  }

  const disabledStyle = disabled || disabledProps || soldout;
  const shouldExpandBenefitZWidth = isBenefitZ && (!disabledStyle || hasRegularButtonHint);

  const testId = `${dataTestId}${disabledStyle ? '-disabled' : ''}${externalSaleLink ? '-link' : ''}`

  return (
    <>
      <Script
        type="text/javascript"
        src="/scripts/widgets/api.bundle.js"
        id="widget-api"
      />
      <div className={classNames(
        style.buyButton__wrapper,
        shouldExpandBenefitZWidth && style.buyButton__wrapper_benefitZ,
        wrapperClassName,
      )}>
        <button
          className={classNames(
            style.buyButton__button,
            empty && style.buyButton__button_empty,
            disabledStyle && style.buyButton__button_disabled,
            isBenefitZ && !disabledStyle && style.buyButton__button_benefitZ,
            shouldExpandBenefitZWidth && style.buyButton__button_benefitZWidth,
            isBenefitZ && disabledStyle && !hasRegularButtonHint && style.buyButton__button_benefitZDisabled,
            buttonClassName,
          )}
          onClick={!empty && onClick ? onButtonClick : _.noop}
          data-test-id={testId}
        >
          <div
            className={classNames(
              style.buyButton__button__icon,
              (empty || disabledStyle) && style.buyButton__button__icon_white,
            )}
          >
            {
              isHotel && <HotelIcon/> ||
              isRestaurant && <RestaurantIcon/> ||
              <TicketIcon/>
            }
          </div>
          {withSeparator && (
            <div
              className={classNames(
                style.buyButton__button__separator,
                (empty || disabledStyle) && style.buyButton__button__separator_white,
              )}
            />
          )}
          <div
            className={classNames([style.buyButton__button__content,
              isRestaurant && style.buyButton__button__content_restaurants])}>
            <>
              {!!price && (
                <div
                  className={classNames(
                    style.buyButton__button__price,
                    (empty || disabledStyle) && style.buyButton__button__price_white,
                  )}
                >
                  {price}
                  {(entityType === 'ticketsbundles' && entity.originalPrice) && (
                    <span className={classNames(
                      style.buyButton__button__originalPrice,
                      (empty || disabledStyle) && style.buyButton__button__originalPrice_white,
                    )}>
                      {priceToString(entity.originalPrice)}
                    </span>
                  )}
                </div>
              )}
              {!!text && (
                <div
                  className={classNames(
                    style.buyButton__button__text,
                    (empty || disabledStyle) && style.buyButton__button__text_white,
                  )}
                >
                  {text}
                </div>
              )}
            </>
          </div>
        </button>
        {!!hintText && (
          <div
            className={classNames(
              style.buyButton__hint,
              style[`buyButton__hint_${hintBackgroundTheme}`],
              extraHintStyles && style[`buyButton__hint_${extraHintStyles}`])}>
            {hintText}
          </div>
        )}
      </div>
      {checkCanOpenMuzaWidget(externalSaleLink) && <MuzaWidgetWrapper link={externalSaleLink} />}
    </>
  );
}
